import { Injectable } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

type UnlockCallback = () => void;

@Injectable({
    providedIn: 'root'
})
export class MediaUnlockService {
    private unlocked = false;
    private unlockSub?: Subscription;
    private callbacks: UnlockCallback[] = [];
    private mediaSet = new Set<HTMLMediaElement>();

    isUnlocked(): boolean {
        return this.unlocked;
    }

    registerMedia(elements: Array<HTMLMediaElement | null | undefined>): void {
        elements.forEach((element) => {
            if (!element) {
                return;
            }
            if (this.mediaSet.has(element)) {
                return;
            }
            this.mediaSet.add(element);
        });

        if (this.unlocked) {
            this.primeMedia(elements);
            return;
        }

        this.initUnlockListener();
    }

    unregisterMedia(elements: Array<HTMLMediaElement | null | undefined>): void {
        elements.forEach((element) => {
            if (element) {
                this.mediaSet.delete(element);
            }
        });
        this.releaseUnlockListenerIfUnused();
    }

    onUnlock(callback: UnlockCallback): () => void {
        if (this.unlocked) {
            callback();
            return () => { };
        }

        let active = true;
        const registeredCallback = () => {
            if (!active) {
                return;
            }

            active = false;
            callback();
        };

        this.callbacks.push(registeredCallback);
        this.initUnlockListener();

        return () => {
            if (!active) {
                return;
            }

            active = false;
            const callbackIndex = this.callbacks.indexOf(registeredCallback);
            if (callbackIndex >= 0) {
                this.callbacks.splice(callbackIndex, 1);
            }
            this.releaseUnlockListenerIfUnused();
        };
    }

    attemptUnlock(): void {
        if (this.unlocked) {
            return;
        }

        this.unlock();
    }

    private initUnlockListener(): void {
        if (this.unlockSub || typeof document === 'undefined') {
            return;
        }

        this.unlockSub = fromEvent(document, 'pointerdown')
            .pipe(take(1))
            .subscribe(() => {
                this.unlockSub = undefined;
                this.unlock();
            });
    }

    private releaseUnlockListenerIfUnused(): void {
        if (this.unlocked || this.mediaSet.size > 0 || this.callbacks.length > 0) {
            return;
        }

        this.unlockSub?.unsubscribe();
        this.unlockSub = undefined;
    }

    private unlock(): void {
        if (this.unlocked) {
            return;
        }

        this.unlocked = true;
        this.primeMedia(Array.from(this.mediaSet));

        const callbacks = [...this.callbacks];
        this.callbacks = [];
        callbacks.forEach((callback) => callback());
    }

    private primeMedia(elements: Array<HTMLMediaElement | null | undefined>): void {
        elements.forEach((element) => {
            if (!element) {
                return;
            }

            if (element instanceof HTMLVideoElement) {
                element.preload = 'none';
                element.setAttribute('preload', 'none');
                (element as any).playsInline = true;
                return;
            }

            element.preload = 'auto';

            // Temporarily mute to avoid any unlock chirp, then restore.
            const originalVolume = element.volume;
            const originalMuted = element.muted;

            element.muted = true;
            element.volume = 0;

            element.load();

            // Do NOT auto-play; just restore original volume/mute so actual playback
            // happens later without chirp.
            element.muted = originalMuted;
            element.volume = originalVolume;
        });
    }
}
